import { useEffect, useMemo } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import {
  CreateProcessFormModel,
  CreateProcessOfferBookingTargetFormModel,
} from '@/views/processesCreate/model/CreateProcessForm.model.ts';
import { useGetCreateOfferDefinitions } from '@/views/processesCreate/util/useGetCreateOfferDefinitions.util.ts';
import {
  ProcessCreateOfferPrecondition,
  ProcessCreateOfferPreconditionParams,
  processCreateOfferUtil,
} from '@/views/processesCreate/utils/processCreateOffer.util.ts';

/**
 * The offer section of the create dialog. `ready` is the only state in which the save sends an offer
 * definition, so the backend creates the offer with a rezerwacja wstępna of the entry; every other state
 * saves the process alone.
 */
export type ProcessCreateOfferState =
  | {
      definitionOptions: SelectSearchOptionModel<number>[];
      itemOptions: SelectSearchOptionModel<number>[];
      status: 'ready';
    }
  | { status: 'error' | 'loading' | 'noDefinitions' }
  | Exclude<ProcessCreateOfferPrecondition, { status: 'bookable' }>;

type Params = ProcessCreateOfferPreconditionParams & {
  selected: CreateProcessOfferBookingTargetFormModel;
  setValue: UseFormSetValue<CreateProcessFormModel>;
};

const isSame = (
  option?: SelectSearchOptionModel<number> | null,
  other?: SelectSearchOptionModel<number> | null,
) => option?.value === other?.value && option?.label === other?.label;

export const useProcessCreateOffer = ({
  selected,
  setValue,
  ...preconditionParams
}: Params): ProcessCreateOfferState => {
  const { type } = preconditionParams;
  const precondition = processCreateOfferUtil.precondition(preconditionParams);
  const { definitions, isError, isLoading } = useGetCreateOfferDefinitions(
    precondition.status === 'bookable',
  );
  const definitionOptions = useMemo(
    () => (type ? processCreateOfferUtil.definitionOptions(definitions, type) : []),
    [definitions, type],
  );
  const onlyDefinition = definitionOptions.length === 1 ? definitionOptions[0] : undefined;
  const chosenDefinition = definitionOptions.find(
    option => option.value === selected.definition?.value,
  );
  const itemOptions = chosenDefinition?.items ?? [];
  const onlyItem = itemOptions.length === 1 ? itemOptions[0] : undefined;
  const chosenItem = itemOptions.find(option => option.value === selected.item?.value);

  // One matching definition or item is chosen for the user; a choice that no longer fits is cleared.
  useEffect(() => {
    const definition = onlyDefinition ?? chosenDefinition ?? null;
    const item = onlyItem ?? chosenItem ?? null;
    if (!isSame(definition, selected.definition)) {
      setValue('offerBookingTarget.definition', definition);
    }
    if (!isSame(item, selected.item)) setValue('offerBookingTarget.item', item);
  }, [
    chosenDefinition,
    chosenItem,
    onlyDefinition,
    onlyItem,
    selected.definition,
    selected.item,
    setValue,
  ]);

  if (precondition.status !== 'bookable') return precondition;
  if (isLoading) return { status: 'loading' };
  if (isError) return { status: 'error' };
  if (definitionOptions.length === 0) return { status: 'noDefinitions' };
  return {
    definitionOptions: definitionOptions.map(({ label, value }) => ({ label, value })),
    itemOptions,
    status: 'ready',
  };
};
